class CustomTooltip {
    constructor(options = {}) {
        this.config = {
            tooltipClass: 'custom-tooltip',
            autoHideDelay: 3000,
            animationDuration: 200,
            hideAnimationDuration: 150,
            offset: 8,
            viewportPadding: 10,
            ...options
        };

        this.activeTooltip = null;
        this.activeTimeout = null;
        this.tooltipId = 0;
        
        this.isTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
        this.isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        
        this.init();
    }

    init() {
        const elements = document.querySelectorAll('[data-text_tip]');
        elements.forEach(element => this.setupElement(element));
        this.setupGlobalListeners();
    }

    setupGlobalListeners() {
        window.addEventListener('scroll', () => this.hideTooltip(), { passive: true });
        window.addEventListener('resize', () => this.hideTooltip(), { passive: true });
        
        document.addEventListener('click', (e) => {
            if (this.activeTooltip && !e.target.hasAttribute('data-text_tip')) {
                this.hideTooltip();
            }
        });
    }

    setupElement(element) {
        this.removeElementListeners(element);
        
        const devTip = element.getAttribute('data-dev_tip') || 'desktop';
        
        switch(devTip) {
            case 'desktop':
                this.setupDesktopTooltip(element);
                break;
            case 'touched':
                this.setupTouchedTooltip(element);
                break;
            case 'mobile':
                this.setupMobileTooltip(element);
                break;
            default:
                this.setupDesktopTooltip(element);
        }
    }

    setupDesktopTooltip(element) {
        if (this.isTouch) return;

        const mouseEnterHandler = (e) => this.showTooltip(e.target);
        const mouseLeaveHandler = (e) => this.hideTooltip();

        element.addEventListener('mouseenter', mouseEnterHandler);
        element.addEventListener('mouseleave', mouseLeaveHandler);
        
        element._tooltipHandlers = { mouseEnterHandler, mouseLeaveHandler };
    }

    setupTouchedTooltip(element) {
        const handler = (e) => {
            this.showTooltip(e.target, true);
        };

        if (!this.isTouch) {
            element.addEventListener('click', handler);
            element._tooltipHandlers = { clickHandler: handler };
        } else {
            element.addEventListener('touchstart', handler);
            element._tooltipHandlers = { touchHandler: handler };
        }
    }

    setupMobileTooltip(element) {
        const handler = (e) => {
            this.showTooltip(e.target, true);
        };

        if (this.isMobile) {
            element.addEventListener('touchstart', handler, { passive: false });
            element._tooltipHandlers = { mobileHandler: handler };
        } else {
            this.setupTouchedTooltip(element);
        }
    }

    showTooltip(element, autoHide = false) {
        this.clearActiveTimeout();
        this.hideTooltip();

        const text = element.getAttribute('data-text_tip');
        const placement = element.getAttribute('data-hover_tip') || 'top';

        if (!text) return;

        const currentTooltipId = ++this.tooltipId;
        const tooltip = this.createTooltipElement(text, placement, currentTooltipId);
        document.body.appendChild(tooltip);

        this.positionTooltip(tooltip, element, placement);
        this.animateTooltipIn(tooltip);
        this.activeTooltip = tooltip;

        if (autoHide) {
            this.activeTimeout = setTimeout(() => {
                if (this.activeTooltip && 
                    this.activeTooltip.dataset.tooltipId == currentTooltipId &&
                    document.body.contains(this.activeTooltip)) {
                    this.hideTooltip();
                }
            }, this.config.autoHideDelay);
        }
    }

    createTooltipElement(text, placement, tooltipId) {
        const tooltip = document.createElement('div');
        tooltip.className = `${this.config.tooltipClass} ${placement}`;
        tooltip.textContent = text;
        tooltip.dataset.tooltipId = tooltipId;
        
        Object.assign(tooltip.style, {
            position: 'absolute',
            zIndex: '1000',
            opacity: '0',
            pointerEvents: 'none',
            background: 'rgba(0, 0, 0, 0.9)',
            color: 'white',
            padding: '0.5rem 0.75rem',
            borderRadius: 'var(--radius-s)',
            fontSize: '0.875rem',
            whiteSpace: 'nowrap',
            backdropFilter: 'blur(4px)',
            transition: 'opacity var(--transition)'
        });

        return tooltip;
    }

    positionTooltip(tooltip, element, placement) {
        const rect = element.getBoundingClientRect();
        const tooltipRect = tooltip.getBoundingClientRect();
        const scrollX = window.scrollX;
        const scrollY = window.scrollY;
        const offset = this.config.offset;

        let top, left;

        switch(placement) {
            case 'top':
                top = rect.top + scrollY - tooltipRect.height - offset;
                left = rect.left + scrollX + (rect.width / 2) - (tooltipRect.width / 2);
                break;
            case 'bottom':
                top = rect.bottom + scrollY + offset;
                left = rect.left + scrollX + (rect.width / 2) - (tooltipRect.width / 2);
                break;
            case 'left':
                top = rect.top + scrollY + (rect.height / 2) - (tooltipRect.height / 2);
                left = rect.left + scrollX - tooltipRect.width - offset;
                break;
            case 'right':
                top = rect.top + scrollY + (rect.height / 2) - (tooltipRect.height / 2);
                left = rect.right + scrollX + offset;
                break;
            default:
                top = rect.top + scrollY - tooltipRect.height - offset;
                left = rect.left + scrollX + (rect.width / 2) - (tooltipRect.width / 2);
        }

        const correctedPosition = this.correctTooltipPosition(top, left, tooltipRect);
        
        tooltip.style.top = `${correctedPosition.top}px`;
        tooltip.style.left = `${correctedPosition.left}px`;
    }

    correctTooltipPosition(top, left, tooltipRect) {
        const padding = this.config.viewportPadding;
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        const scrollX = window.scrollX;
        const scrollY = window.scrollY;

        if (left < padding) {
            left = padding;
        } else if (left + tooltipRect.width > viewportWidth - padding) {
            left = viewportWidth - tooltipRect.width - padding;
        }

        if (top < scrollY + padding) {
            top = scrollY + padding;
        } else if (top + tooltipRect.height > scrollY + viewportHeight - padding) {
            top = scrollY + viewportHeight - tooltipRect.height - padding;
        }

        return { top, left };
    }

    animateTooltipIn(tooltip) {
        if (tooltip.animate) {
            tooltip.animate([
                { 
                    opacity: 0, 
                    transform: 'scale(0.8) translateY(10px)',
                    filter: 'blur(4px)'
                },
                { 
                    opacity: 1, 
                    transform: 'scale(1) translateY(0px)',
                    filter: 'blur(0px)'
                }
            ], {
                duration: this.config.animationDuration,
                easing: 'cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                fill: 'forwards'
            });
        } else {
            tooltip.style.opacity = '1';
        }
    }

    hideTooltip() {
        if (this.activeTooltip) {
            this.clearActiveTimeout();
            
            const tooltipToHide = this.activeTooltip;
            this.activeTooltip = null;
            
            if (tooltipToHide.animate) {
                const hideAnimation = tooltipToHide.animate([
                    { 
                        opacity: 1, 
                        transform: 'scale(1) translateY(0px)',
                        filter: 'blur(0px)'
                    },
                    { 
                        opacity: 0, 
                        transform: 'scale(0.8) translateY(-10px)',
                        filter: 'blur(4px)'
                    }
                ], {
                    duration: this.config.hideAnimationDuration,
                    easing: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                    fill: 'forwards'
                });

                hideAnimation.addEventListener('finish', () => {
                    this.removeTooltipFromDOM(tooltipToHide);
                });
            } else {
                this.removeTooltipFromDOM(tooltipToHide);
            }
        }
    }

    removeTooltipFromDOM(tooltip) {
        if (tooltip && tooltip.parentNode) {
            tooltip.parentNode.removeChild(tooltip);
        }
    }

    clearActiveTimeout() {
        if (this.activeTimeout) {
            clearTimeout(this.activeTimeout);
            this.activeTimeout = null;
        }
    }

    removeElementListeners(element) {
        if (element._tooltipHandlers) {
            const handlers = element._tooltipHandlers;
            
            if (handlers.mouseEnterHandler) {
                element.removeEventListener('mouseenter', handlers.mouseEnterHandler);
                element.removeEventListener('mouseleave', handlers.mouseLeaveHandler);
            }
            if (handlers.clickHandler) {
                element.removeEventListener('click', handlers.clickHandler);
            }
            if (handlers.touchHandler) {
                element.removeEventListener('touchstart', handlers.touchHandler);
            }
            if (handlers.mobileHandler) {
                element.removeEventListener('touchstart', handlers.mobileHandler);
            }
            
            delete element._tooltipHandlers;
        }
    }

    cleanup() {
        this.clearActiveTimeout();
        
        if (this.activeTooltip) {
            this.removeTooltipFromDOM(this.activeTooltip);
            this.activeTooltip = null;
        }

        const elements = document.querySelectorAll('[data-text_tip]');
        elements.forEach(element => this.removeElementListeners(element));
    }

    reinit() {
        this.cleanup();
        this.init();
    }

    addTooltip(element, text, placement = 'top', devTip = 'desktop') {
        if (!element || !text) return false;

        element.setAttribute('data-text_tip', text);
        element.setAttribute('data-hover_tip', placement);
        element.setAttribute('data-dev_tip', devTip);
        this.setupElement(element);
        return true;
    }

    updateTooltip(element, text, placement, devTip) {
        if (!element) return false;

        if (text !== undefined) element.setAttribute('data-text_tip', text);
        if (placement !== undefined) element.setAttribute('data-hover_tip', placement);
        if (devTip !== undefined) element.setAttribute('data-dev_tip', devTip);
        
        this.setupElement(element);
        return true;
    }

    removeTooltip(element) {
        if (!element) return false;

        this.removeElementListeners(element);
        element.removeAttribute('data-text_tip');
        element.removeAttribute('data-hover_tip');
        element.removeAttribute('data-dev_tip');
        return true;
    }
}

export default CustomTooltip;